import * as React from 'react';
import { Link } from 'react-router';
import { AccountsError, resendSignup, signUp, signupOptions, type SignupOptions } from '../accounts.ts';
import { AUTH_LINK, AUTH_SUBMIT, AuthField, AuthNotice, AuthShell } from './AuthShell.tsx';

export const SIGNUP_MIN_PASSWORD = 12;
export const SIGNUP_SENT_TEXT =
  'We sent you an email. If you are new here, open its link and enter the password you just chose; it works for 24 hours. If you already have an account, sign in or reset your password.';

/**
 * Self sign-up (ADR 0044 §3). It creates the account and mails a confirmation link; there is no session until
 * the link is used. Where the deployment sets terms of use, a tick box links to them.
 */
export function SignUpPage() {
  const [options, setOptions] = React.useState<SignupOptions | null>(null);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [terms, setTerms] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState('');
  const [again, setAgain] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    void signupOptions().then((o) => !cancelled && setOptions(o));
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < SIGNUP_MIN_PASSWORD) return setError(`At least ${SIGNUP_MIN_PASSWORD} characters.`);
    if (password !== confirm) return setError('The two passwords differ.');
    setBusy(true);
    setError('');
    try {
      await signUp({
        email: email.trim(),
        password,
        ...(options?.termsUrl ? { accept_terms: terms } : {}),
      });
      setSent(email.trim());
    } catch (err) {
      setError(err instanceof AccountsError ? err.message : 'Sign-up failed.');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setAgain('');
    try {
      await resendSignup(sent);
      setAgain('We sent the link again.');
    } catch (err) {
      setError(err instanceof AccountsError ? err.message : 'The request failed.');
    }
  };

  return (
    <AuthShell
      title="Create an account"
      subtitle="We will email you a link to confirm it"
      testId="signup-page"
    >
      {options && !options.enabled ? (
        <div className="flex flex-col gap-5">
          <AuthNotice kind="error" testId="signup-closed">
            Sign-up is not open here. Ask for an invitation.
          </AuthNotice>
          <Link to="/login" className={AUTH_LINK}>
            Back to sign in
          </Link>
        </div>
      ) : sent ? (
        <div className="flex flex-col gap-5">
          <AuthNotice kind="ok" testId="signup-sent">
            {SIGNUP_SENT_TEXT}
          </AuthNotice>
          {again && (
            <AuthNotice kind="ok" testId="signup-again">
              {again}
            </AuthNotice>
          )}
          {error && (
            <AuthNotice kind="error" testId="signup-error">
              {error}
            </AuthNotice>
          )}
          <button
            type="button"
            onClick={() => void resend()}
            data-testid="signup-resend"
            className={AUTH_LINK}
          >
            Send the link again
          </button>
          <Link to="/login" className={AUTH_LINK}>
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          {error && (
            <AuthNotice kind="error" testId="signup-error">
              {error}
            </AuthNotice>
          )}
          <AuthField
            id="signup-email"
            label="Email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            testId="signup-email"
          />
          <AuthField
            id="signup-password"
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            minLength={SIGNUP_MIN_PASSWORD}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            testId="signup-password"
          />
          <AuthField
            id="signup-confirm"
            label="Confirm the password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            testId="signup-confirm"
          />
          {options?.termsUrl && (
            <label className="flex items-start gap-2 text-sm text-page-text">
              <input
                type="checkbox"
                checked={terms}
                required
                onChange={(e) => setTerms(e.target.checked)}
                data-testid="signup-terms"
                className="mt-0.5"
              />
              <span>
                I accept the{' '}
                <a href={options.termsUrl} target="_blank" rel="noreferrer" className={AUTH_LINK}>
                  terms of use
                </a>
              </span>
            </label>
          )}
          <button
            type="submit"
            disabled={busy || !options}
            data-testid="signup-submit"
            className={AUTH_SUBMIT}
          >
            {busy ? 'Creating…' : 'Create account'}
          </button>
          <Link to="/login" className={AUTH_LINK}>
            Already have an account? Sign in
          </Link>
        </form>
      )}
    </AuthShell>
  );
}
